import { installHaptics } from "@pax/ui";
import { QueryClientProvider } from "@tanstack/react-query";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouteError } from "@/components/RouteError";
import { isActingAs } from "@/features/auth/act-as";
import {
  checkInSessions,
  clearAccountQueryData,
  type Session,
  sessionKey,
  sessionQuery,
  synchronizeAuthAcrossTabs,
} from "@/features/auth/queries";
import { startActivityHeartbeats } from "@/lib/activity";
import { registerAuthorizationLossHandler, registerRefusalHandler } from "@/lib/api";
import { createQueryClient } from "@/lib/query-client";
import { createRefusalHandler } from "@/lib/risk";
import { screenTravel } from "@/lib/screen-travel";
import { startBrowserTelemetry } from "@/lib/telemetry";
import { SCREEN_SCROLLER } from "@/shell/Screen";
import { routeTree } from "./routeTree.gen";
import "./styles.css";

startBrowserTelemetry();
const queryClient = createQueryClient();
const stopRefusalHandler = registerRefusalHandler(
  createRefusalHandler(
    () => {
      const session = queryClient.getQueryData<Session | null>(sessionKey);
      return { accountId: session?.user.id ?? null, actingAsPlayer: isActingAs(session) };
    },
    () => void queryClient.invalidateQueries({ queryKey: sessionKey, exact: true }),
  ),
);
const stopSessionCheckIns = checkInSessions(queryClient);
if (navigator.onLine) void queryClient.query(sessionQuery).catch(() => undefined);

const router = createRouter({
  routeTree,
  context: { queryClient },
  // Route components read from the query cache, so a loader's job is to get
  // the fetch going, not to hold the navigation. Hovering a card starts the
  // fetch before the press lands.
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: RouteError,
  // The leaving screen becomes a compositor snapshot for the length of the
  // travel, so only one route is ever mounted and the arriving one renders
  // its skeleton once instead of racing a live tree it is sliding over.
  defaultViewTransition: { types: screenTravel },
  // Going back lands on the store at the scroll it was left at, before the
  // covers have even repainted. Opening something starts at the top.
  scrollRestoration: true,
  scrollToTopSelectors: [SCREEN_SCROLLER],
});

const stopWaitingForFirstScreen = router.subscribe("onResolved", () => {
  stopWaitingForFirstScreen();
  void startActivityHeartbeats(
    () => {
      const screen = router.state.matches.at(-1);
      const params: Partial<Record<string, string>> = screen?.params ?? {};
      return {
        screen: screen?.fullPath ?? "unmatched",
        gameId: params.gameId ?? null,
      };
    },
    () => Boolean(queryClient.getQueryData<Session | null>(sessionKey)),
  );
});

const stopHaptics = installHaptics();
const stopAuthSynchronization = synchronizeAuthAcrossTabs(queryClient, () => router.invalidate());
// A request the API answered 401 reads the session again. The current answer stands until the new
// one arrives, so the window only changes if the session really did.
const stopAuthorizationLossHandler = registerAuthorizationLossHandler(async () => {
  clearAccountQueryData(queryClient);
  await queryClient.refetchQueries({ queryKey: sessionKey, exact: true });
  await router.invalidate();
});
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    stopHaptics();
    stopAuthSynchronization();
    stopAuthorizationLossHandler();
    stopRefusalHandler();
    stopSessionCheckIns();
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const container = document.getElementById("root");
if (!container) throw new Error("index.html is missing #root");

// Once React owns the page, route head metadata owns the title.
document.querySelector("title[data-shell]")?.remove();

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
);
